import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { HandHeart, Plus, Globe, Church, Trash2, Flag, MessageCircle, Package, HeartHandshake, HelpingHand, Clock, CheckCircle, XCircle, Gift, Send, ChevronDown, ChevronUp, Share2, Smile, Image as ImageIcon, Loader2, X, Pencil } from "lucide-react";
import { getRequests, createRequest, togglePrayRequest, deleteRequest, reportContent, searchUsers, uploadImage, resolveImageUrl, updateRequest } from "@/lib/api";
import DOMPurify from 'dompurify';
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import CatholicEmojiPicker, { renderTextWithCatholicEmojis } from "@/components/CatholicEmojiPicker";

// Types for requests (all types available)
export const REQUEST_TYPES = [
  { key: "prayer", label: "Prayer", icon: HandHeart, color: "var(--liturgical-primary)", action: "pray" },
  { key: "donation", label: "Financial Help", icon: HeartHandshake, color: "#556B2F", action: "message" },
  { key: "item", label: "Item", icon: Package, color: "#D4AF37", action: "message" },
  { key: "service", label: "Service", icon: HelpingHand, color: "#2D5A7B", action: "message" }
];

// Types for offers (no Financial/Donation, no Prayer)
export const OFFER_TYPES = [
  { key: "item", label: "Item", icon: Package, color: "#D4AF37", action: "message" },
  { key: "service", label: "Service", icon: HelpingHand, color: "#2D5A7B", action: "message" }
];

// Create Request Dialog
function CreateRequestDialog({ user, onCreated }) {
  const [itemType, setItemType] = useState("prayer");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [scope, setScope] = useState("parish");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [neverExpires, setNeverExpires] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [imageUrls, setImageUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const reqFileInputRef = useRef(null);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const res = await uploadImage(file);
        const url = res.data?.url || res.data?.data?.url;
        if (url) {
          setImageUrls(prev => [...prev, url]);
        }
      }
    } catch { toast.error("Failed to upload image"); }
    setUploading(false);
    if (reqFileInputRef.current) reqFileInputRef.current.value = "";
  };

  const getPlaceholder = () => {
    switch (itemType) {
      case "prayer": return "Share your prayer intention...";
      case "donation": return "Describe what you need help with financially...";
      case "item": return "Describe the item(s) you're looking for...";
      case "service": return "Describe the help or service you need...";
      default: return "Describe your request...";
    }
  };

  const getTitlePlaceholder = () => {
    switch (itemType) {
      case "prayer": return "e.g., Prayer for healing";
      case "donation": return "e.g., Help with medical bills";
      case "item": return "e.g., Looking for baby clothes";
      case "service": return "e.g., Need help with yard work";
      default: return "What do you need?";
    }
  };

  const handleSubmit = async () => {
    if (!title.trim() || !content.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      await createRequest({
        request_type: itemType,
        listing_type: "request",
        title,
        content,
        scope,
        is_anonymous: isAnonymous,
        never_expires: neverExpires,
        image_urls: imageUrls.length > 0 ? imageUrls : undefined
      });
      toast.success("Request shared with your community!");
      setOpen(false);
      setTitle("");
      setContent("");
      setIsAnonymous(false);
      setNeverExpires(false);
      setItemType("prayer");
      setImageUrls([]);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create request");
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!uploading) setOpen(v); }}>
      <DialogTrigger asChild>
        <Button className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold" data-testid="create-request-btn">
          <HandHeart className="w-4 h-4 mr-2" /> New Request
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-lg max-h-[90vh] overflow-y-auto" onPointerDownOutside={e => { if (uploading) e.preventDefault(); }} onInteractOutside={e => { if (uploading) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-2xl text-[#2D2A26]">
            Ask for Help
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Category Type Selection */}
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">What do you need?</Label>
            <div className="grid grid-cols-2 gap-2">
              {REQUEST_TYPES.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.key}
                    onClick={() => setItemType(type.key)}
                    className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                      itemType === type.key
                        ? "border-[var(--liturgical-primary)] bg-[var(--liturgical-primary)]/5"
                        : "border-[#E5E0D5] hover:border-[var(--liturgical-primary)]/50"
                    }`}
                    data-testid={`request-type-${type.key}`}
                  >
                    <Icon className="w-5 h-5" style={{ color: type.color }} />
                    <span className="text-sm font-['Nunito'] font-medium text-[#2D2A26]">{type.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={getTitlePlaceholder()}
              className="border-[#E5E0D5] font-['Nunito'] text-sm focus-visible:ring-[var(--liturgical-primary)]"
              data-testid="request-title-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Description</Label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={getPlaceholder()}
              className="border-[#E5E0D5] font-['Nunito'] text-sm min-h-[100px] focus-visible:ring-[var(--liturgical-primary)]"
              data-testid="request-content-input"
            />
          </div>

          {/* Image uploads */}
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Photos (optional)</Label>
            <div className="flex flex-wrap gap-2">
              {imageUrls.map((url, idx) => (
                <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-[#E5E0D5]">
                  <img src={resolveImageUrl(url)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setImageUrls(imageUrls.filter((_, i) => i !== idx))} className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {imageUrls.length < 5 && (
                <button
                  type="button"
                  onClick={() => reqFileInputRef.current?.click()}
                  className="w-20 h-20 rounded-lg border-2 border-dashed border-[#E5E0D5] hover:border-[var(--liturgical-primary)] flex flex-col items-center justify-center text-[#6B665E] hover:text-[var(--liturgical-primary)] transition-colors"
                  disabled={uploading}
                  data-testid="request-add-photo"
                >
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ImageIcon className="w-5 h-5" /><span className="text-[10px] mt-0.5">Add</span></>}
                </button>
              )}
            </div>
            <input ref={reqFileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleImageUpload} />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Share with</Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger className="w-40 border-[#E5E0D5] text-sm font-['Nunito']" data-testid="request-scope-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#E5E0D5]">
                  <SelectItem value="global" className="font-['Nunito'] text-sm">
                    <span className="flex items-center gap-1.5"><Globe className="w-3 h-3" /> Everyone</span>
                  </SelectItem>
                  {user.parish_id && (
                    <SelectItem value="parish" className="font-['Nunito'] text-sm">
                      <span className="flex items-center gap-1.5"><Church className="w-3 h-3" /> My Parish</span>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="font-['Nunito'] text-xs text-[#6B665E]">Anonymous</Label>
              <Switch
                checked={isAnonymous}
                onCheckedChange={setIsAnonymous}
                className="data-[state=checked]:bg-[var(--liturgical-primary)]"
                data-testid="request-anonymous-switch"
              />
            </div>
          </div>

          {isAnonymous && (
            <p className="text-xs text-[#6B665E] font-['Nunito'] bg-[#F9F7F2] p-2 rounded">
              People can respond to your anonymous request through replies on the post.
            </p>
          )}

          <div className="flex items-center justify-between p-3 bg-[#F9F7F2] rounded-lg">
            <div>
              <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Keep open indefinitely</Label>
              <p className="text-[10px] text-[#6B665E] font-['Nunito']">
                {neverExpires ? "Will stay open until you close it" : "Expires after 30 days"}
              </p>
            </div>
            <Switch
              checked={neverExpires}
              onCheckedChange={setNeverExpires}
              className="data-[state=checked]:bg-[var(--liturgical-primary)]"
              data-testid="request-never-expires-switch"
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold h-10"
            data-testid="request-submit-btn"
          >
            {loading ? "Sharing..." : "Share Request"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Create Offer Dialog
function CreateOfferDialog({ user, onCreated }) {
  const [itemType, setItemType] = useState("item");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [scope, setScope] = useState("parish");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [neverExpires, setNeverExpires] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [imageUrls, setImageUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const res = await uploadImage(file);
        const url = res.data?.url || res.data?.data?.url;
        if (url) {
          setImageUrls(prev => [...prev, url]);
        }
      }
    } catch { toast.error("Failed to upload image"); }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const getPlaceholder = () => {
    switch (itemType) {
      case "item": return "Describe the item(s) you're offering (e.g., baby clothes, furniture, car seats)...";
      case "service": return "Describe the service you're offering (e.g., tutoring, yard work, childcare)...";
      default: return "Describe what you're offering...";
    }
  };

  const getTitlePlaceholder = () => {
    switch (itemType) {
      case "item": return "e.g., Free baby clothes (0-6 months)";
      case "service": return "e.g., Offering homeschool tutoring";
      default: return "What are you offering?";
    }
  };

  const handleSubmit = async () => {
    if (!title.trim() || !content.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      await createRequest({
        request_type: itemType,
        listing_type: "offer",
        title,
        content,
        scope,
        is_anonymous: isAnonymous,
        never_expires: neverExpires,
        image_urls: imageUrls.length > 0 ? imageUrls : undefined
      });
      toast.success("Offer shared with your community!");
      setOpen(false);
      setTitle("");
      setContent("");
      setIsAnonymous(false);
      setNeverExpires(false);
      setItemType("item");
      setImageUrls([]);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create offer");
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!uploading) setOpen(v); }}>
      <DialogTrigger asChild>
        <Button className="bg-[#556B2F] hover:brightness-90 text-white font-['Nunito'] font-semibold" data-testid="create-offer-btn">
          <Gift className="w-4 h-4 mr-2" /> New Offer
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-lg max-h-[90vh] overflow-y-auto" onPointerDownOutside={e => { if (uploading) e.preventDefault(); }} onInteractOutside={e => { if (uploading) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-2xl text-[#2D2A26]">
            Offer to Help
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Category Type Selection - Only Item and Service for offers */}
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">What are you offering?</Label>
            <div className="grid grid-cols-2 gap-2">
              {OFFER_TYPES.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.key}
                    onClick={() => setItemType(type.key)}
                    className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                      itemType === type.key
                        ? "border-[#556B2F] bg-[#556B2F]/5"
                        : "border-[#E5E0D5] hover:border-[#556B2F]/50"
                    }`}
                    data-testid={`offer-type-${type.key}`}
                  >
                    <Icon className="w-5 h-5" style={{ color: type.color }} />
                    <span className="text-sm font-['Nunito'] font-medium text-[#2D2A26]">{type.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={getTitlePlaceholder()}
              className="border-[#E5E0D5] font-['Nunito'] text-sm focus-visible:ring-[#556B2F]"
              data-testid="offer-title-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Description</Label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={getPlaceholder()}
              className="border-[#E5E0D5] font-['Nunito'] text-sm min-h-[100px] focus-visible:ring-[#556B2F]"
              data-testid="offer-content-input"
            />
          </div>

          {/* Image uploads */}
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Photos (optional)</Label>
            <div className="flex flex-wrap gap-2">
              {imageUrls.map((url, idx) => (
                <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-[#E5E0D5]">
                  <img src={resolveImageUrl(url)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setImageUrls(imageUrls.filter((_, i) => i !== idx))} className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {imageUrls.length < 5 && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-20 h-20 rounded-lg border-2 border-dashed border-[#E5E0D5] hover:border-[#556B2F] flex flex-col items-center justify-center text-[#6B665E] hover:text-[#556B2F] transition-colors"
                  disabled={uploading}
                  data-testid="offer-add-photo"
                >
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ImageIcon className="w-5 h-5" /><span className="text-[10px] mt-0.5">Add</span></>}
                </button>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleImageUpload} />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Share with</Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger className="w-40 border-[#E5E0D5] text-sm font-['Nunito']" data-testid="offer-scope-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#E5E0D5]">
                  <SelectItem value="global" className="font-['Nunito'] text-sm">
                    <span className="flex items-center gap-1.5"><Globe className="w-3 h-3" /> Everyone</span>
                  </SelectItem>
                  {user.parish_id && (
                    <SelectItem value="parish" className="font-['Nunito'] text-sm">
                      <span className="flex items-center gap-1.5"><Church className="w-3 h-3" /> My Parish</span>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="font-['Nunito'] text-xs text-[#6B665E]">Anonymous</Label>
              <Switch
                checked={isAnonymous}
                onCheckedChange={setIsAnonymous}
                className="data-[state=checked]:bg-[#556B2F]"
                data-testid="offer-anonymous-switch"
              />
            </div>
          </div>

          {isAnonymous && (
            <p className="text-xs text-[#6B665E] font-['Nunito'] bg-[#F9F7F2] p-2 rounded">
              People can respond to your anonymous offer through replies on the post.
            </p>
          )}

          <div className="flex items-center justify-between p-3 bg-[#F9F7F2] rounded-lg">
            <div>
              <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Keep open indefinitely</Label>
              <p className="text-[10px] text-[#6B665E] font-['Nunito']">
                {neverExpires ? "Will stay open until you close it" : "Expires after 30 days"}
              </p>
            </div>
            <Switch
              checked={neverExpires}
              onCheckedChange={setNeverExpires}
              className="data-[state=checked]:bg-[#556B2F]"
              data-testid="offer-never-expires-switch"
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full bg-[#556B2F] hover:brightness-90 text-white font-['Nunito'] font-semibold h-10"
            data-testid="offer-submit-btn"
          >
            {loading ? "Sharing..." : "Share Offer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Reply component for listings with emoji, media, and @mention support
export function ListingReplies({ listing, user }) {
  const navigate = useNavigate();
  const [replies, setReplies] = useState([]);
  const [content, setContent] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [repliesLoaded, setRepliesLoaded] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState("");
  const [mentionResults, setMentionResults] = useState([]);
  const [searchingMentions, setSearchingMentions] = useState(false);
  const [mentionedUsers, setMentionedUsers] = useState([]);
  const [cursorPosition, setCursorPosition] = useState(0);
  const fileInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const mentionRef = useRef(null);
  const inputRef = useRef(null);

  // Close emoji picker and mention dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
      if (mentionRef.current && !mentionRef.current.contains(e.target)) {
        setShowMentions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Search for users when mentionSearch changes
  useEffect(() => {
    if (mentionSearch.length < 1) {
      setMentionResults([]);
      return;
    }
    const searchTimeout = setTimeout(async () => {
      setSearchingMentions(true);
      try {
        const res = await searchUsers(mentionSearch, { limit: 5 });
        const users = res.data?.users || res.data || [];
        setMentionResults(users);
      } catch (err) {
        console.error("Mention search failed:", err);
      }
      setSearchingMentions(false);
    }, 200);
    return () => clearTimeout(searchTimeout);
  }, [mentionSearch]);

  const loadReplies = async () => {
    if (repliesLoaded) return;
    try {
      const res = await api.get(`/requests/${listing.request_id}/replies`);
      setReplies(res.data.replies || []);
      setRepliesLoaded(true);
    } catch (err) {
      console.error("Failed to load replies:", err);
    }
  };

  const handleToggleReplies = () => {
    if (!showReplies && !repliesLoaded) {
      loadReplies();
    }
    setShowReplies(!showReplies);
  };

  const handleContentChange = (e) => {
    const value = e.target.value;
    const cursor = e.target.selectionStart;
    setContent(value);
    setCursorPosition(cursor);
    const textBeforeCursor = value.substring(0, cursor);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    if (mentionMatch) {
      setShowMentions(true);
      setMentionSearch(mentionMatch[1]);
    } else {
      setShowMentions(false);
      setMentionSearch("");
    }
  };

  const insertMention = (mentionUser) => {
    const textBeforeCursor = content.substring(0, cursorPosition);
    const textAfterCursor = content.substring(cursorPosition);
    const mentionStart = textBeforeCursor.lastIndexOf("@");
    const beforeMention = content.substring(0, mentionStart);
    const mentionText = `@${mentionUser.name.replace(/\s+/g, "_")}`;
    const newContent = beforeMention + mentionText + " " + textAfterCursor;
    setContent(newContent);
    setShowMentions(false);
    setMentionSearch("");
    if (!mentionedUsers.find(m => m.user_id === mentionUser.user_id)) {
      setMentionedUsers([...mentionedUsers, { user_id: mentionUser.user_id, name: mentionUser.name }]);
    }
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadImage(file);
      const url = res.data?.url || res.data?.data?.url;
      if (url) {
        setImageUrl(url);
      }
    } catch (err) {
      toast.error("Failed to upload image");
    }
    setUploading(false);
  };

  const handleEmojiClick = (emojiData) => {
    if (emojiData.emoji) {
      setContent(prev => prev + emojiData.emoji);
    }
  };

  const handleSubmitReply = async () => {
    if (!content.trim() && !imageUrl) return;
    setLoading(true);
    try {
      const res = await api.post(`/requests/${listing.request_id}/replies`, { 
        content: content.trim(),
        media_url: imageUrl,
        mentioned_users: mentionedUsers
      });
      setReplies([...replies, res.data]);
      setContent("");
      setImageUrl(null);
      setMentionedUsers([]);
      toast.success("Reply posted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post reply");
    }
    setLoading(false);
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  // Render text with mentions highlighted
  const renderReplyContent = (text) => {
    if (!text) return null;
    // First handle Catholic emojis
    let html = renderTextWithCatholicEmojis(text);
    // Then highlight @mentions
    html = html.replace(/@(\w+)/g, '<span class="text-[var(--liturgical-primary)] font-semibold cursor-pointer hover:underline">@$1</span>');
    return <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />;
  };

  return (
    <div className="border-t border-[#E5E0D5]/50 pt-3 mt-3">
      <button
        onClick={handleToggleReplies}
        className="flex items-center gap-2 text-sm text-[#6B665E] hover:text-[#2D2A26] font-['Nunito'] transition-colors"
        data-testid={`toggle-replies-${listing.request_id}`}
      >
        {showReplies ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        {listing.reply_count || 0} {listing.reply_count === 1 ? 'Reply' : 'Replies'}
      </button>

      {showReplies && (
        <div className="mt-3 space-y-3">
          {/* Existing replies */}
          {replies.map((reply) => (
            <div key={reply.reply_id} className="flex gap-2 pl-2 border-l-2 border-[#E5E0D5]">
              <Avatar className="h-7 w-7 cursor-pointer" onClick={() => navigate(`/profile/${reply.author_id}`)}>
                <AvatarImage src={reply.author_picture} />
                <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-xs">
                  {reply.author_name?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span 
                    className="text-sm font-medium text-[#2D2A26] font-['Nunito'] cursor-pointer hover:underline"
                    onClick={() => navigate(`/profile/${reply.author_id}`)}
                  >
                    {reply.author_name}
                  </span>
                  <span className="text-xs text-[#6B665E] font-['Nunito']">{timeAgo(reply.created_at)}</span>
                </div>
                <div className="text-sm text-[#2D2A26] font-['Nunito']">{renderReplyContent(reply.content)}</div>
                {reply.media_url && (
                  <img 
                    src={resolveImageUrl(reply.media_url)} 
                    alt="" 
                    className="mt-2 rounded-md max-h-48 object-cover border border-[#E5E0D5]"
                  />
                )}
              </div>
            </div>
          ))}

          {/* Image preview */}
          {imageUrl && (
            <div className="relative inline-block">
              <img src={resolveImageUrl(imageUrl)} alt="" className="h-16 rounded-md border border-[#E5E0D5]" />
              <button
                onClick={() => setImageUrl(null)}
                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}

          {/* Enhanced reply input with emoji and media */}
          <div className="flex gap-2 items-end">
            <div className="flex-1 relative">
              <div className="flex gap-1 items-center border border-[#E5E0D5] rounded-md bg-white px-2">
                {/* Emoji picker */}
                <div className="relative" ref={emojiPickerRef}>
                  <button
                    type="button"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="p-1 text-[#6B665E] hover:text-[var(--liturgical-primary)]"
                    title="Add emoji"
                    data-testid={`reply-emoji-btn-${listing.request_id}`}
                  >
                    <Smile className="w-4 h-4" />
                  </button>
                  {showEmojiPicker && (
                    <div className="absolute bottom-8 left-0 z-50">
                      <CatholicEmojiPicker onEmojiClick={handleEmojiClick} width={280} height={300} />
                    </div>
                  )}
                </div>
                
                {/* Image upload */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="p-1 text-[#6B665E] hover:text-[var(--liturgical-primary)]"
                  title="Add image"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
                
                {/* Text input */}
                <input
                  ref={inputRef}
                  value={content}
                  onChange={handleContentChange}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && !showMentions && handleSubmitReply()}
                  placeholder="Write a reply... (use @ to mention)"
                  className="flex-1 text-xs py-2 bg-transparent font-['Nunito'] focus:outline-none"
                  data-testid={`reply-input-${listing.request_id}`}
                />
              </div>
              
              {/* Mention dropdown */}
              {showMentions && (
                <div 
                  ref={mentionRef}
                  className="absolute bottom-full left-0 mb-1 w-full bg-white border border-[#E5E0D5] rounded-md shadow-lg z-50 max-h-[200px] overflow-y-auto"
                >
                  {searchingMentions ? (
                    <div className="flex items-center justify-center py-3">
                      <Loader2 className="w-4 h-4 animate-spin text-[var(--liturgical-primary)]" />
                    </div>
                  ) : mentionResults.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-[#6B665E] font-['Nunito']">
                      {mentionSearch.length === 0 ? "Type a name..." : "No users found"}
                    </div>
                  ) : (
                    mentionResults.map((mentionUser) => (
                      <button
                        key={mentionUser.user_id}
                        onClick={() => insertMention(mentionUser)}
                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#F0EBE0] text-left"
                      >
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={mentionUser.picture} />
                          <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-xs">
                            {mentionUser.name?.[0]?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm font-['Nunito'] text-[#2D2A26]">{mentionUser.name}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            
            <Button
              size="sm"
              onClick={handleSubmitReply}
              disabled={loading || (!content.trim() && !imageUrl)}
              className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white h-9 px-3"
              data-testid={`reply-submit-${listing.request_id}`}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function ListingCard({ listing, user, onPray, onDelete, onShare, onEdit }) {
  const navigate = useNavigate();
  const allTypes = [...REQUEST_TYPES, ...OFFER_TYPES];
  const typeConfig = allTypes.find(t => t.key === listing.request_type) || REQUEST_TYPES[0];
  const TypeIcon = typeConfig.icon;
  const isOffer = listing.listing_type === "offer";
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const formatExpiry = (expiresAt) => {
    if (!expiresAt) return null;
    const expiry = new Date(expiresAt);
    const now = new Date();
    const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return "Expired";
    if (daysLeft === 0) return "Expires today";
    if (daysLeft === 1) return "Expires tomorrow";
    return `${daysLeft} days left`;
  };

  const handleReport = async () => {
    if (window.confirm("Report this as inappropriate?")) {
      try {
        await reportContent({ content_type: "request", content_id: listing.request_id, reason: "inappropriate" });
        toast.success("Report submitted");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to report");
      }
    }
  };

  const handleMessageAuthor = () => {
    if (listing.is_anonymous) {
      toast.info("Use the reply section below to respond to anonymous posts");
      return;
    }
    navigate(`/messages/${listing.author_id}`);
  };

  return (
    <Card className="bg-white border-[#E5E0D5] shadow-sm hover:shadow-md transition-all duration-300" data-testid={`listing-card-${listing.request_id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Avatar
              className={`h-10 w-10 border border-[#E5E0D5] ${!listing.is_anonymous ? 'cursor-pointer' : ''}`}
              onClick={() => !listing.is_anonymous && navigate(`/profile/${listing.author_id}`)}
            >
              <AvatarImage src={listing.author_picture} />
              <AvatarFallback className="text-white text-sm font-['Nunito']" style={{ backgroundColor: isOffer ? "#556B2F" : typeConfig.color }}>
                {listing.is_anonymous ? "?" : listing.author_name?.[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <span className="font-['Nunito'] font-semibold text-sm text-[#2D2A26]">
                {listing.is_anonymous ? "Anonymous" : listing.author_name}
              </span>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-[#6B665E] font-['Nunito']">{timeAgo(listing.created_at)}</span>
                {listing.scope === "global" ? (
                  <Globe className="w-3 h-3 text-[#6B665E]" />
                ) : (
                  <Church className="w-3 h-3 text-[#6B665E]" />
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap justify-end">
            {/* Offer/Request Badge */}
            <Badge 
              className="text-white text-[10px] font-['Nunito']"
              style={{ backgroundColor: isOffer ? "#556B2F" : "var(--liturgical-primary)" }}
            >
              {isOffer ? <Gift className="w-3 h-3 mr-1" /> : <HandHeart className="w-3 h-3 mr-1" />}
              {isOffer ? "Offering" : "Request"}
            </Badge>
            {/* Type Badge */}
            <Badge 
              variant="outline"
              className="text-[10px] font-['Nunito'] border-[#E5E0D5]"
              style={{ color: typeConfig.color }}
            >
              <TypeIcon className="w-3 h-3 mr-1" />
              {typeConfig.label}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26]">{listing.title}</h3>
        <p className="text-sm text-[#2D2A26] font-['Nunito'] leading-relaxed whitespace-pre-wrap">{listing.content}</p>
        
        {/* Images with lightbox */}
        {listing.image_urls && listing.image_urls.length > 0 && (
          <div className={`grid gap-2 ${listing.image_urls.length === 1 ? '' : 'grid-cols-2'}`}>
            {listing.image_urls.slice(0, 4).map((url, i) => (
              <img 
                key={i} 
                src={url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url.startsWith('/') ? '' : '/'}${url}`} 
                alt={`Request image ${i + 1}`} 
                className="rounded-lg w-full h-48 object-cover border border-[#E5E0D5] cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => { setLightboxImage(url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url.startsWith('/') ? '' : '/'}${url}`); setLightboxOpen(true); }}
                data-testid={`listing-image-${listing.request_id}-${i}`}
              />
            ))}
          </div>
        )}
        
        {/* Expiration info */}
        {!listing.never_expires && listing.expires_at && (
          <div className="flex items-center gap-1.5 text-xs text-[#6B665E] font-['Nunito']">
            <Clock className="w-3 h-3" />
            {formatExpiry(listing.expires_at)}
          </div>
        )}

        {/* Status badges */}
        {listing.status === "fulfilled" && (
          <Badge className="bg-[var(--liturgical-primary)] text-white text-xs">
            <CheckCircle className="w-3 h-3 mr-1" /> Fulfilled
          </Badge>
        )}
        {listing.status === "closed" && (
          <Badge className="bg-[#6B665E] text-white text-xs">
            <XCircle className="w-3 h-3 mr-1" /> Closed
          </Badge>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-[#E5E0D5]/50">
          <div className="flex items-center gap-2">
            {/* Action button based on type */}
            {listing.request_type === "prayer" ? (
              <button
                onClick={() => onPray(listing.request_id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-['Nunito'] font-medium transition-all ${
                  listing.user_prayed
                    ? "bg-[var(--liturgical-primary)]/10 text-[var(--liturgical-primary)]"
                    : "bg-[#F9F7F2] text-[#6B665E] hover:bg-[var(--liturgical-primary)]/5 hover:text-[var(--liturgical-primary)]"
                }`}
                data-testid={`listing-pray-btn-${listing.request_id}`}
              >
                <HandHeart className={`w-4 h-4 ${listing.user_prayed ? "fill-current" : ""}`} />
                {listing.user_prayed ? "Praying" : "I'll Pray"}
                {listing.pray_count > 0 && (
                  <Badge variant="secondary" className="ml-1 bg-white text-[var(--liturgical-primary)] text-xs font-['Nunito']">
                    {listing.pray_count}
                  </Badge>
                )}
              </button>
            ) : (
              <button
                onClick={handleMessageAuthor}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-['Nunito'] font-medium transition-all ${
                  isOffer
                    ? "bg-[#556B2F]/10 text-[#556B2F] hover:bg-[#556B2F]/20"
                    : "bg-[var(--liturgical-primary)]/10 text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/20"
                }`}
                data-testid={`listing-message-btn-${listing.request_id}`}
              >
                <MessageCircle className="w-4 h-4" />
                {listing.is_anonymous ? "Reply Below" : isOffer ? "I'm Interested" : "Message to Help"}
              </button>
            )}
          </div>

          <div className="flex items-center gap-1">
            {/* Share button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onShare(listing)}
              className="h-8 w-8 p-0 text-[#6B665E] hover:text-[var(--liturgical-primary)]"
              title="Share to message"
              data-testid={`listing-share-${listing.request_id}`}
            >
              <Share2 className="w-4 h-4" />
            </Button>
            {listing.author_id === user.user_id && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(listing)}
                  className="h-8 w-8 p-0 text-[#6B665E] hover:text-[var(--liturgical-primary)]"
                  title="Edit"
                  data-testid={`listing-edit-${listing.request_id}`}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(listing.request_id)}
                  className="h-8 w-8 p-0 text-[#6B665E] hover:text-[#991B1B] hover:bg-red-50"
                  data-testid={`listing-delete-${listing.request_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReport}
              className="h-8 w-8 p-0 text-[#6B665E] hover:text-[var(--liturgical-secondary)]"
              data-testid={`listing-report-${listing.request_id}`}
            >
              <Flag className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Replies section - allows responding to anonymous posts */}
        <ListingReplies listing={listing} user={user} />

        {/* Image Lightbox */}
        <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
          <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 bg-black/95 border-none">
            <DialogHeader className="sr-only"><DialogTitle>Image Preview</DialogTitle></DialogHeader>
            <button 
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="flex items-center justify-center w-full h-full min-h-[50vh]">
              <img src={lightboxImage} alt="" className="max-w-full max-h-[85vh] object-contain" />
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default function RequestsPage({ user }) {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [listingTypeFilter, setListingTypeFilter] = useState("all"); // all, request, offer
  const [feedTab, setFeedTab] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  
  // Share dialog state
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [shareSearchQuery, setShareSearchQuery] = useState("");
  const [shareSearchResults, setShareSearchResults] = useState([]);
  const [shareSearching, setShareSearching] = useState(false);
  const [sharing, setSharing] = useState(false);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editListing, setEditListing] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [editImageUrls, setEditImageUrls] = useState([]);
  const [editUploading, setEditUploading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const editFileInputRef = useRef(null);

  const loadListings = async () => {
    setLoading(true);
    try {
      const params = { scope: feedTab };
      if (typeFilter !== "all") {
        params.request_type = typeFilter;
      }
      if (listingTypeFilter !== "all") {
        params.listing_type = listingTypeFilter;
      }
      const res = await getRequests(params);
      setListings(res.data);
    } catch (err) {
      console.error("Failed to load listings:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadListings();
  }, [feedTab, typeFilter, listingTypeFilter]);
  
  // Search users for share dialog
  useEffect(() => {
    if (shareSearchQuery.length < 1) {
      setShareSearchResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setShareSearching(true);
      try {
        const res = await searchUsers(shareSearchQuery, { limit: 10 });
        setShareSearchResults(res.data?.users || res.data || []);
      } catch (err) {
        console.error("Share search failed:", err);
      }
      setShareSearching(false);
    }, 300);
    return () => clearTimeout(timeout);
  }, [shareSearchQuery]);

  const handlePray = async (requestId) => {
    try {
      const res = await togglePrayRequest(requestId);
      setListings(
        listings.map((r) =>
          r.request_id === requestId
            ? {
                ...r,
                user_prayed: res.data.is_praying,
                pray_count: res.data.pray_count
              }
            : r
        )
      );
    } catch (err) {
      toast.error("Failed to update prayer");
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm("Delete this?")) return;
    try {
      await deleteRequest(requestId);
      setListings(listings.filter((r) => r.request_id !== requestId));
      toast.success("Deleted successfully");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };
  
  const handleShare = (listing) => {
    setShareTarget(listing);
    setShowShareDialog(true);
  };

  const handleEdit = (listing) => {
    setEditListing(listing);
    setEditTitle(listing.title || "");
    setEditContent(listing.content || "");
    setEditImageUrls(listing.image_urls || []);
    setEditDialogOpen(true);
  };

  const handleEditImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setEditUploading(true);
    try {
      for (const file of files) {
        const res = await uploadImage(file);
        const url = res.data?.url || res.data?.data?.url;
        if (url) setEditImageUrls(prev => [...prev, url]);
      }
    } catch { toast.error("Failed to upload image"); }
    setEditUploading(false);
    if (editFileInputRef.current) editFileInputRef.current.value = "";
  };

  const handleSaveEdit = async () => {
    if (!editListing || !editTitle.trim() || !editContent.trim()) return;
    setEditSaving(true);
    try {
      await updateRequest(editListing.request_id, {
        title: editTitle,
        content: editContent,
        image_urls: editImageUrls
      });
      toast.success("Updated successfully");
      setEditDialogOpen(false);
      setEditListing(null);
      loadListings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
    setEditSaving(false);
  };
  
  const handleShareToUser = async (recipientUser) => {
    if (!shareTarget || !recipientUser) return;
    setSharing(true);
    try {
      const isOffer = shareTarget.listing_type === "offer";
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/requests?id=${shareTarget.request_id}`;
      const message = `Check out this ${isOffer ? 'offer' : 'request'}: "${shareTarget.title}"\n\n${link}`;
      
      await api.post("/messages/send", {
        receiver_id: recipientUser.user_id,
        content: message
      });
      toast.success(`Shared with ${recipientUser.name}`);
      setShowShareDialog(false);
      setShareTarget(null);
      setShareSearchQuery("");
    } catch (err) {
      toast.error("Failed to share");
    }
    setSharing(false);
  };

  // Get filtered types based on current listing type filter
  const getAvailableTypes = () => {
    if (listingTypeFilter === "offer") {
      return OFFER_TYPES;
    }
    return REQUEST_TYPES;
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="requests-page">
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-['Crimson_Text'] text-3xl font-bold text-[#2D2A26] tracking-tight" data-testid="requests-title">
              Offers & Requests
            </h1>
            <p className="text-sm text-[#6B665E] font-['Nunito'] mt-1">Share needs and offer help to your community</p>
          </div>
          <div className="flex gap-2">
            <CreateRequestDialog user={user} onCreated={loadListings} />
            <CreateOfferDialog user={user} onCreated={loadListings} />
          </div>
        </div>

        {/* Offer/Request Toggle */}
        <div className="flex gap-2 p-1 bg-[#F0EBE0] rounded-lg border border-[#E5E0D5] w-fit">
          <button
            onClick={() => setListingTypeFilter("all")}
            className={`px-4 py-2 rounded-md text-sm font-['Nunito'] font-medium transition-all ${
              listingTypeFilter === "all"
                ? "bg-white text-[#2D2A26] shadow-sm"
                : "text-[#6B665E] hover:text-[#2D2A26]"
            }`}
            data-testid="filter-listing-all"
          >
            All
          </button>
          <button
            onClick={() => setListingTypeFilter("request")}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-['Nunito'] font-medium transition-all ${
              listingTypeFilter === "request"
                ? "bg-white shadow-sm"
                : "text-[#6B665E] hover:text-[#2D2A26]"
            }`}
            style={listingTypeFilter === "request" ? { color: "var(--liturgical-primary)" } : {}}
            data-testid="filter-listing-request"
          >
            <HandHeart className="w-4 h-4" />
            Requests
          </button>
          <button
            onClick={() => setListingTypeFilter("offer")}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-['Nunito'] font-medium transition-all ${
              listingTypeFilter === "offer"
                ? "bg-white shadow-sm"
                : "text-[#6B665E] hover:text-[#2D2A26]"
            }`}
            style={listingTypeFilter === "offer" ? { color: "#556B2F" } : {}}
            data-testid="filter-listing-offer"
          >
            <Gift className="w-4 h-4" />
            Offers
          </button>
        </div>

        {/* Type Filter */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={typeFilter === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setTypeFilter("all")}
            className={typeFilter === "all" ? "bg-[#2D2A26] text-white" : "border-[#E5E0D5] text-[#6B665E]"}
            data-testid="filter-all"
          >
            All Types
          </Button>
          {getAvailableTypes().map((type) => {
            const Icon = type.icon;
            return (
              <Button
                key={type.key}
                variant={typeFilter === type.key ? "default" : "outline"}
                size="sm"
                onClick={() => setTypeFilter(type.key)}
                className={typeFilter === type.key ? "text-white" : "border-[#E5E0D5] text-[#6B665E]"}
                style={typeFilter === type.key ? { backgroundColor: type.color } : {}}
                data-testid={`filter-${type.key}`}
              >
                <Icon className="w-4 h-4 mr-1" />
                {type.label}
              </Button>
            );
          })}
        </div>

        {/* Scope Tabs - matching the feed pattern */}
        <Tabs value={feedTab} onValueChange={setFeedTab} data-testid="listing-tabs">
          <TabsList className="bg-[#F0EBE0] border border-[#E5E0D5]">
            <TabsTrigger
              value="all"
              className="font-['Nunito'] text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]"
              data-testid="listing-tab-all"
            >
              All
            </TabsTrigger>
            {(user.parish_id || (user.parishes && user.parishes.length > 0)) && (
              <TabsTrigger
                value="parish"
                className="font-['Nunito'] text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]"
                data-testid="listing-tab-parish"
              >
                <Church className="w-3.5 h-3.5 mr-1.5" />
                {user.parishes && user.parishes.length > 1 ? "My Parishes" : "My Parish"}
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>

        {/* Listings */}
        {loading ? (
          <div className="text-center py-16 text-[#6B665E] font-['Nunito']">Loading...</div>
        ) : listings.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-listings">
            {listingTypeFilter === "offer" ? (
              <>
                <Gift className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
                <p className="text-[#6B665E] font-['Nunito']">No offers yet. Be the first to offer something!</p>
              </>
            ) : listingTypeFilter === "request" ? (
              <>
                <HandHeart className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
                <p className="text-[#6B665E] font-['Nunito']">No requests yet. Share your first request!</p>
              </>
            ) : (
              <>
                <HeartHandshake className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
                <p className="text-[#6B665E] font-['Nunito']">No offers or requests yet. Start by sharing one!</p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {listings.map((listing) => (
              <ListingCard
                key={listing.request_id}
                listing={listing}
                user={user}
                onPray={handlePray}
                onDelete={handleDelete}
                onShare={handleShare}
                onEdit={handleEdit}
              />
            ))}
          </div>
        )}
        
        {/* Share Dialog */}
        <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
          <DialogContent className="bg-white border-[#E5E0D5]">
            <DialogHeader>
              <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">
                Share to Message
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {shareTarget && (
                <div className="p-3 bg-[#F9F7F2] rounded-md border border-[#E5E0D5]">
                  <p className="text-sm font-['Nunito'] text-[#6B665E] mb-1">
                    {shareTarget.listing_type === "offer" ? "Sharing offer:" : "Sharing request:"}
                  </p>
                  <p className="font-['Nunito'] font-medium text-[#2D2A26]">"{shareTarget.title}"</p>
                </div>
              )}
              
              <div>
                <Label className="font-['Nunito'] text-[#6B665E]">Search for a user to share with</Label>
                <Input
                  value={shareSearchQuery}
                  onChange={(e) => setShareSearchQuery(e.target.value)}
                  placeholder="Type a name..."
                  className="mt-1.5 border-[#E5E0D5] font-['Nunito']"
                  data-testid="share-search-input"
                />
              </div>
              
              {shareSearching ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-[var(--liturgical-primary)]" />
                </div>
              ) : shareSearchResults.length > 0 ? (
                <div className="max-h-[200px] overflow-y-auto space-y-1">
                  {shareSearchResults.map((u) => (
                    <button
                      key={u.user_id}
                      onClick={() => handleShareToUser(u)}
                      disabled={sharing}
                      className="w-full flex items-center gap-3 p-2 hover:bg-[#F0EBE0] rounded-md transition-colors"
                      data-testid={`share-user-${u.user_id}`}
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={u.picture} />
                        <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-sm">
                          {u.name?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-['Nunito'] text-[#2D2A26]">{u.name}</span>
                      {sharing && <Loader2 className="w-4 h-4 animate-spin ml-auto" />}
                    </button>
                  ))}
                </div>
              ) : shareSearchQuery.length > 0 ? (
                <p className="text-sm text-[#6B665E] font-['Nunito'] text-center py-4">No users found</p>
              ) : null}
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Listing Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={(v) => { if (!editUploading) setEditDialogOpen(v); }}>
          <DialogContent className="bg-white border-[#E5E0D5] max-w-md max-h-[85vh] overflow-y-auto" onPointerDownOutside={e => { if (editUploading) e.preventDefault(); }} onInteractOutside={e => { if (editUploading) e.preventDefault(); }}>
            <DialogHeader>
              <DialogTitle className="font-['Crimson_Text'] text-2xl text-[#2D2A26]">
                Edit {editListing?.listing_type === "offer" ? "Offer" : "Request"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="space-y-1.5">
                <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Title</label>
                <Input 
                  value={editTitle} 
                  onChange={e => setEditTitle(e.target.value)} 
                  className="border-[#E5E0D5] font-['Nunito'] text-sm"
                  data-testid="edit-listing-title"
                />
              </div>
              <div className="space-y-1.5">
                <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Content</label>
                <Textarea 
                  value={editContent} 
                  onChange={e => setEditContent(e.target.value)} 
                  className="border-[#E5E0D5] font-['Nunito'] text-sm min-h-[120px]"
                  data-testid="edit-listing-content"
                />
              </div>
              {/* Photos */}
              <div className="space-y-1.5">
                <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Photos</label>
                <div className="flex flex-wrap gap-2">
                  {editImageUrls.map((url, idx) => (
                    <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-[#E5E0D5]">
                      <img src={resolveImageUrl(url)} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setEditImageUrls(prev => prev.filter((_, i) => i !== idx))}
                        className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {editImageUrls.length < 5 && (
                    <button type="button" onClick={() => editFileInputRef.current?.click()}
                      className="w-20 h-20 rounded-lg border-2 border-dashed border-[#E5E0D5] hover:border-[var(--liturgical-primary)] flex flex-col items-center justify-center text-[#6B665E] hover:text-[var(--liturgical-primary)] transition-colors"
                      disabled={editUploading}>
                      {editUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ImageIcon className="w-5 h-5" /><span className="text-[10px] mt-0.5">Add</span></>}
                    </button>
                  )}
                </div>
                <input ref={editFileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleEditImageUpload} />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1 border-[#E5E0D5] font-['Nunito']">Cancel</Button>
                <Button 
                  onClick={handleSaveEdit} 
                  disabled={editSaving || !editTitle.trim() || !editContent.trim()} 
                  className="flex-1 bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
                  data-testid="save-edit-listing"
                >
                  {editSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
